import AppContext from '@/AppContext';
import CustomAlert from '@/components/common/CustomAlert';
import logoSquare from '@/images/logo_lektuarium_pl.webp';
import { isBooleanString } from '@/lib/utils';
import { isValidDomain } from '@/utils/Regex';
import { EyeIcon, EyeOffIcon } from '@heroicons/react/outline';
import { Button, Input } from '@heroui/react';
import Image from 'next/image';
import Link from 'next/link';
import { useContext, useMemo, useState } from 'react';

export default function Register() {
  const [formData, setFormData] = useState({
    email: '',
    password1: '',
    password2: ''
  });
  const [isVisible, setIsVisible] = useState(false);
  const [errors, setErrors] = useState({
    password: null,
    email: null
  });
  const [isRegistered, setIsRegistered] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  const context = useContext(AppContext);
  const domain = useMemo(() => `https://${formData.email.split('@')[1]}`, [formData.email]);

  const successMessage = {
    title: 'Rejestracja przebiegła pomyślnie! Potwierdź konto klikając w link, który znajdziesz w swojej skrzynce mailowej. Sprawdź również folder ze spamem.',
    type: 'success',
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'email') {
      setErrors(prev => ({ ...prev, email: null }));
    } else {
      validatePasswords(name === 'password1' ? value : formData.password1,
                        name === 'password2' ? value : formData.password2);
    }
  };

  const validatePasswords = (pass1, pass2) => {
    if (pass1 && pass2 && pass1 !== pass2) {
      setErrors(prev => ({ ...prev, password: 'Hasła muszą być identyczne.' }));
    } else {
      setErrors(prev => ({ ...prev, password: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password1 !== formData.password2) {
      validatePasswords(formData.password1, formData.password2);
      return;
    }

    setIsRegistering(true);

    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_DJANGO_URL}/api/auth/users/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            email: formData.email,
            password: formData.password1
          }).toString(),
        }
      );

      if (!response.ok) {
        const json = await response.json();
        setErrors({
          email: json.email?.[0] || null,
          password: json.password?.[0] || null
        });
      } else if (response.status === 201) {
        setIsRegistered(true);
      }
    } catch (error) {
      context.setNotification({ type: 'warning' });
    } finally {
      setIsRegistering(false);
    }
  };

  return (
    <>
      <div className="flex min-h-full bg-white">
        <div className="flex flex-1 flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-sm lg:w-96">
            <div>
              <Link href="/" shallow>
                <Image className="h-12 w-12" src={logoSquare} alt="Workflow" fill={false} />
              </Link>
              <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
                Dołącz do społeczności
              </h2>
            </div>

            <div className="mt-8">
              <div className="mt-6">
                <form onSubmit={handleSubmit} className="space-y-6" autoComplete="off">
                  <Input
                    isRequired
                    label="Email Address"
                    name="email"
                    placeholder="Enter your email"
                    type="email"
                    variant="bordered"
                    autoComplete="email"
                    onChange={handleInputChange}
                    value={formData.email}
                    errorMessage={errors.email}
                    isInvalid={isBooleanString(errors.email)}
                  />

                  <Input
                    isRequired
                    endContent={
                      <button type="button" onClick={() => setIsVisible(!isVisible)}>
                        {isVisible ? (
                          <EyeIcon
                            className="h-5 my-auto w-5 pointer-events-none text-2xl text-default-400"
                          />
                        ) : (
                          <EyeOffIcon
                            className="h-5 my-auto w-5 pointer-events-none text-2xl text-default-400"
                          />
                        )}
                      </button>
                    }
                    value={formData.password1}
                    onChange={handleInputChange}
                    label="Password"
                    name="password1"
                    placeholder="Enter your password"
                    type={isVisible ? "text" : "password"}
                    onBlur={() => validatePasswords(formData.password1, formData.password2)}
                    variant="bordered"
                    errorMessage={errors.password}
                    isInvalid={isBooleanString(errors.password)}
                  />

                  <Input
                    isRequired
                    endContent={
                      <button type="button" onClick={() => setIsVisible(!isVisible)}>
                        {isVisible ? (
                          <EyeIcon
                            className="h-5 my-auto w-5 pointer-events-none text-2xl text-default-400"
                          />
                        ) : (
                          <EyeOffIcon
                            className="h-5 my-auto w-5 pointer-events-none text-2xl text-default-400"
                          />
                        )}
                      </button>
                    }
                    value={formData.password2}
                    onChange={handleInputChange}
                    label="Repeat Password"
                    name="password2"
                    placeholder="Enter your password"
                    type={isVisible ? "text" : "password"}
                    onBlur={() => validatePasswords(formData.password1, formData.password2)}
                    variant="bordered"
                    errorMessage={errors.password}
                    isInvalid={isBooleanString(errors.password)}
                  />
                  {isRegistered && <CustomAlert color={successMessage.type} title={successMessage.title} variant="flat" />}
                  <div>
                    <Button
                      data-umami-event="Register"
                      disabled={isRegistered}
                      type="submit"
                      className="flex w-full justify-center rounded-md border border-transparent bg-blue-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-25"
                      isLoading={isRegistering}
                    >
                      Zarejestruj
                    </Button>

                    {isRegistered && isValidDomain(domain) && (
                      <Link
                        target="_blank"
                        href={domain}
                        className="my-1 flex w-full justify-center rounded-md border border-transparent bg-green-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-25"
                      >
                        Przejdź do swojej poczty
                      </Link>
                    )}
                  </div>
                </form>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center">
              </div>
            </div>
            <div>
              <div className="relative mt-6">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-2 text-gray-500">Masz już konto?</span>
                </div>
              </div>
              <div className="mt-2 text-right">
                <Link
                  href="/auth/login"
                  className="text-sm font-medium text-blue-600 hover:text-blue-500"
                >
                  Kliknij tu aby się zalogować
                </Link>
              </div>
            </div>
          </div>
        </div >
        <div className="relative hidden w-0 flex-1 lg:block">
          <Image
            className="absolute inset-0 h-full w-full object-cover"
            src="https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1908&q=80"
            alt=""
            fill
          />
        </div>
      </div >
    </>
  );
}

Register.layout = 'Auth';
